import { useEffect } from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { LoginPage } from './pages/LoginPage';
import { ShellLayout } from './pages/ShellLayout';
import { OperatorsListPage } from './pages/OperatorsListPage';
import { AircraftListPage } from './pages/AircraftListPage';
import { AircraftFormPage } from './pages/aircraft-form/AircraftFormPage';
import { BookingsListPage } from './pages/bookings-list/BookingsListPage';
import { BookingDetailPage } from './pages/booking-detail/BookingDetailPage';
import { AirportsListPage } from './pages/airports/AirportsListPage';
import { SettingsPage } from './pages/SettingsPage';
import { ChartersListPage } from './pages/charters-list/ChartersListPage';
import { ChartersDetailPage } from './pages/charter-detail/ChartersDetailPage';
import { CharterBookingsListPage } from './pages/CharterBookingsListPage';
import { CharterBookingDetailPage } from './pages/charter-booking-detail/CharterBookingDetailPage';
import { useAuth } from './hooks/useAuth';
import { me } from './api/endpoints';

function AuthGate({ children }: { children: React.ReactNode }) {
  const token = useAuth((s) => s.token);
  const admin = useAuth((s) => s.admin);
  const setSession = useAuth((s) => s.setSession);
  const clear = useAuth((s) => s.clear);
  const location = useLocation();

  // If we have a token but no admin in memory yet, fetch /auth/me to hydrate.
  //
  // SHAR-1099 (regression 28 Sep) — the check is keyed by the TOKEN it checks.
  // Keyed as plain ['me'], the "/auth/me failed" result of an expired token
  // stayed in the query cache; after a successful login the gate re-mounted,
  // read that cached error and cleared the brand-new session, so the next
  // request went out without a token, 401'd and bounced back to login - a loop
  // until a reload emptied the cache. A new token now has its own (empty)
  // cache entry, and only a failure while the session is still unconfirmed
  // (no admin yet) may clear it.
  const meQuery = useQuery({
    queryKey: ['me', token],
    queryFn: me,
    enabled: !!token && !admin,
    retry: false,
    gcTime: 0,
  });
  useEffect(() => {
    if (meQuery.data && token) setSession(token, meQuery.data);
    if (meQuery.isError && !admin) clear();
  }, [meQuery.data, meQuery.isError, token, admin, setSession, clear]);

  if (!token) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}

export function Router_() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <AuthGate>
              <ShellLayout />
            </AuthGate>
          }
        >
          <Route index element={<Navigate to="/bookings" replace />} />
          <Route path="operators" element={<OperatorsListPage />} />
          <Route path="aircraft" element={<AircraftListPage />} />
          <Route path="aircraft/new" element={<AircraftFormPage />} />
          <Route path="aircraft/:id" element={<AircraftFormPage />} />
          <Route path="bookings" element={<BookingsListPage />} />
          <Route path="bookings/:id" element={<BookingDetailPage />} />
          <Route path="airports" element={<AirportsListPage />} />
          <Route path="charters" element={<ChartersListPage />} />
          <Route path="charters/:id" element={<ChartersDetailPage />} />
          <Route path="charter-bookings" element={<CharterBookingsListPage />} />
          <Route path="charter-bookings/:id" element={<CharterBookingDetailPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export { Router_ as Router };
